import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAdminWs } from '../context/AdminWsContext';
import {
  RefreshCw, Search, Activity, Wrench, Trash2,
  ChevronDown, ChevronUp, FileArchive, Download,
  CheckCircle, AlertCircle, Clock, X, CreditCard,
} from 'lucide-react';

const fmt     = (n) => new Intl.NumberFormat('ru-RU').format(n || 0);
const fmtDate = (d) => d
  ? new Date(d).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

/* ── Toast ───────────────────────────────────────────────────────────────── */
const Toast = ({ message, type = 'info', onClose }) => (
  <div className={`toast toast-${type}`}>
    {type === 'success' && <CheckCircle size={14} style={{ color: 'var(--green)',  flexShrink: 0 }} />}
    {type === 'error'   && <AlertCircle size={14} style={{ color: 'var(--red)',    flexShrink: 0 }} />}
    {type === 'info'    && <Clock       size={14} style={{ color: 'var(--brand)',  flexShrink: 0 }} />}
    <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-1)' }}>{message}</span>
    <button className="btn-icon" style={{ width: '24px', height: '24px', border: 'none' }} onClick={onClose}>
      <X size={12} />
    </button>
  </div>
);

/* ── Diagnostics panel ───────────────────────────────────────────────────── */
const DiagPanel = ({ device, onRequestDiag, diagReady, fetchAuthorized, relayUrl }) => {
  const [files, setFiles]     = useState(null);
  const [loading, setLoading] = useState(false);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetchAuthorized('/api/dashboard/diagnostics');
      const data = await res.json();
      if (data.success) setFiles(data.files.filter(f => f.name.includes(device.device_id)));
    } catch {}
    finally { setLoading(false); }
  }, [device.device_id, fetchAuthorized]);

  useEffect(() => { loadFiles(); }, [loadFiles]);
  useEffect(() => { if (diagReady) loadFiles(); }, [diagReady, loadFiles]);

  const rows = [
    ['OS',              device.os_version],
    ['Принтер',         device.printer_name],
    ['Сканер',          device.scanner_status],
    ['Зарегистрирован', fmtDate(device.registered_at)],
  ];

  return (
    <tr>
      <td colSpan={5} style={{ padding: 0, background: 'rgba(129,140,248,0.03)' }}>
        <div style={{ padding: '20px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>

          {/* Details */}
          <div>
            <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Детали</p>
            {rows.map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px', fontSize: '12px' }}>
                <span className="text-muted">{k}</span>
                <span style={{ fontWeight: '500' }}>{v || '—'}</span>
              </div>
            ))}
          </div>

          {/* Revenue */}
          <div>
            <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Прибыль</p>
            {[
              ['За 30 дней',   `${fmt(device.revenue_30d)} UZS`],
              ['Заказов 30д',  device.orders_30d ?? '—'],
              ['Всего листов', fmt(device.total_prints_count)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px', fontSize: '12px' }}>
                <span className="text-muted">{k}</span>
                <span style={{ fontWeight: '600' }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Diagnostics */}
          <div>
            <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Диагностика</p>
            <button className="btn" style={{ width: '100%', justifyContent: 'center', marginBottom: '10px', fontSize: '12px' }} onClick={() => onRequestDiag(device.device_id)}>
              <Wrench size={12} /> Запросить логи
            </button>
            {diagReady && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--green)', marginBottom: '8px' }}>
                <CheckCircle size={12} /> Новые логи готовы
              </div>
            )}
            {loading && <p style={{ fontSize: '12px' }} className="text-dim">Загрузка...</p>}
            {!loading && files?.length === 0 && <p style={{ fontSize: '12px' }} className="text-dim">Нет архивов</p>}
            {files?.map(f => (
              <a
                key={f.name}
                href={`${relayUrl}/api/dashboard/diagnostics/${encodeURIComponent(f.name)}`}
                target="_blank" rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--brand)', textDecoration: 'none', marginBottom: '4px' }}
              >
                <FileArchive size={11} />
                <span className="truncate" style={{ flex: 1 }}>{f.name}</span>
                <Download size={10} />
              </a>
            ))}
          </div>
        </div>
      </td>
    </tr>
  );
};

/* ── Device row ──────────────────────────────────────────────────────────── */
const DeviceRow = ({ device, onDelete, onRequestDiag, diagReady, fetchAuthorized, relayUrl, onPaymentToggle }) => {
  const [expanded, setExpanded]           = useState(false);
  const [paymentToggling, setPaymentToggling] = useState(false);

  const handlePaymentToggle = async () => {
    setPaymentToggling(true);
    try { await onPaymentToggle(device.device_id, device.payment_enabled); }
    finally { setPaymentToggling(false); }
  };

  const printerOk = device.printer_status === 'ok' || device.printer_status === 'Ready';

  return (
    <>
      <tr>
        <td>
          <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>
            {device.machine_name || 'Unnamed Kiosk'}
          </div>
          <div style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }} className="text-dim">
            <Activity size={9} />
            <span className="mono">{device.device_id?.slice(0, 14)}</span>
          </div>
        </td>

        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <span className={`dot ${device.is_online ? 'dot-online' : 'dot-offline'}`} />
            <span style={{ fontSize: '13px', color: device.is_online ? 'var(--green)' : 'var(--text-2)' }}>
              {device.is_online ? 'Онлайн' : 'Оффлайн'}
            </span>
          </div>
          <div style={{ fontSize: '11px', marginTop: '2px' }} className="text-dim">{fmtDate(device.last_seen)}</div>
        </td>

        <td>
          <span style={{ fontSize: '13px', color: printerOk ? 'var(--green)' : device.printer_status ? 'var(--yellow)' : 'var(--text-3)' }}>
            {device.printer_status || '—'}
          </span>
        </td>

        <td>
          <span style={{ fontWeight: '600' }}>{fmt(device.revenue_30d)}</span>
          <span className="text-dim" style={{ fontSize: '11px' }}> UZS</span>
          <div className="text-dim" style={{ fontSize: '11px', marginTop: '1px' }}>{device.orders_30d ?? 0} заказов</div>
        </td>

        <td>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {diagReady && <span className="dot dot-warning" title="Диагностика готова" />}

            <button
              className="btn-icon"
              title={device.payment_enabled ? 'Оплата вкл — отключить' : 'Оплата выкл — включить'}
              style={{ width: 'auto', padding: '4px 8px', borderRadius: '6px', gap: '4px', fontSize: '11px', display: 'flex', alignItems: 'center', color: device.payment_enabled ? 'var(--green)' : 'var(--text-3)', opacity: paymentToggling ? 0.5 : 1 }}
              onClick={handlePaymentToggle}
              disabled={paymentToggling}
            >
              <CreditCard size={12} />
              {device.payment_enabled ? 'Вкл' : 'Выкл'}
            </button>

            <button className="btn-icon" title="Детали" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>

            <button className="btn-icon" title="Удалить" style={{ color: 'var(--red)' }} onClick={() => onDelete(device)}>
              <Trash2 size={13} />
            </button>
          </div>
        </td>
      </tr>

      {expanded && (
        <DiagPanel
          device={device}
          onRequestDiag={onRequestDiag}
          diagReady={diagReady}
          fetchAuthorized={fetchAuthorized}
          relayUrl={relayUrl}
        />
      )}
    </>
  );
};

/* ── Delete modal ────────────────────────────────────────────────────────── */
const DeleteModal = ({ device, onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
    <div className="card" style={{ width: '360px', padding: '28px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--red)' }}>
        <AlertCircle size={17} /> Удалить киоск?
      </h3>
      <p style={{ fontSize: '13px', marginBottom: '6px' }}>
        Устройство: <strong>{device.machine_name || device.device_id}</strong>
      </p>
      <p className="text-dim" style={{ fontSize: '12px', marginBottom: '24px' }}>
        Будут удалены все заказы и сессии этого устройства. Действие необратимо.
      </p>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onCancel}>Отмена</button>
        <button className="btn btn-danger" onClick={() => onConfirm(device.device_id)}>
          <Trash2 size={13} /> Удалить
        </button>
      </div>
    </div>
  </div>
);

/* ── Main ────────────────────────────────────────────────────────────────── */
export const Devices = () => {
  const { fetchAuthorized, relayUrl } = useAuth();
  const [devices, setDevices]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [toDelete, setToDelete]   = useState(null);
  const [toasts, setToasts]       = useState([]);
  const [diagReady, setDiagReady] = useState({});
  const toastId = useRef(0);

  const addToast = useCallback((message, type = 'info') => {
    const id = ++toastId.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetchAuthorized('/api/dashboard/devices');
      const data = await res.json();
      if (data.success) setDevices(data.devices);
    } catch { addToast('Ошибка загрузки устройств', 'error'); }
    finally { setLoading(false); }
  }, [fetchAuthorized, addToast]);

  const handleWs = useCallback((msg) => {
    if (msg.event === 'device_online') {
      setDevices(prev => prev.map(d =>
        d.device_id === msg.data.device_id ? { ...d, is_online: true, last_seen: new Date().toISOString() } : d
      ));
    }
    if (msg.event === 'device_offline') {
      setDevices(prev => prev.map(d =>
        d.device_id === msg.data.device_id ? { ...d, is_online: false } : d
      ));
    }
    if (msg.event === 'device_telemetry') {
      setDevices(prev => prev.map(d =>
        d.device_id === msg.data.device_id
          ? { ...d, is_online: true, last_seen: new Date().toISOString(), printer_status: msg.data.printer_status ?? d.printer_status }
          : d
      ));
    }
    if (msg.event === 'diagnostics_ready') {
      setDiagReady(prev => ({ ...prev, [msg.data.device_id]: true }));
      addToast(`Диагностика готова: ${msg.data.file_name || msg.data.device_id}`, 'success');
    }
  }, [addToast]);

  useAdminWs(handleWs);
  useEffect(() => { loadDevices(); }, [loadDevices]);

  const handleRequestDiag = async (deviceId) => {
    try {
      const res = await fetchAuthorized(`/api/dashboard/devices/${deviceId}/diagnostics/request`, { method: 'POST' });
      if (res.ok) {
        addToast('Запрос диагностики отправлен', 'info');
        setDiagReady(prev => ({ ...prev, [deviceId]: false }));
      } else {
        addToast('Не удалось отправить запрос', 'error');
      }
    } catch { addToast('Ошибка соединения', 'error'); }
  };

  const handleDelete = async (deviceId) => {
    try {
      const res = await fetchAuthorized(`/api/dashboard/devices/${deviceId}`, { method: 'DELETE' });
      if (res.ok) {
        setDevices(prev => prev.filter(d => d.device_id !== deviceId));
        addToast('Киоск удалён', 'success');
      } else {
        addToast('Ошибка при удалении', 'error');
      }
    } catch { addToast('Ошибка соединения', 'error'); }
    setToDelete(null);
  };

  const handlePaymentToggle = async (deviceId) => {
    try {
      const res  = await fetchAuthorized(`/api/dashboard/devices/${deviceId}/payment-toggle`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, payment_enabled: data.payment_enabled } : d));
        addToast(`Оплата ${data.payment_enabled ? 'включена' : 'выключена'}`, 'success');
      } else {
        addToast('Ошибка переключения оплаты', 'error');
      }
    } catch { addToast('Ошибка соединения', 'error'); }
  };

  const filtered = devices.filter(d =>
    (d.device_id || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.machine_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fade-in">
      {/* Toasts */}
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => setToasts(prev => prev.filter(x => x.id !== t.id))} />
        ))}
      </div>

      {toDelete && <DeleteModal device={toDelete} onConfirm={handleDelete} onCancel={() => setToDelete(null)} />}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Киоски</h1>
          <p>Мониторинг и управление устройствами</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
            <input
              placeholder="Поиск..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '200px', paddingLeft: '32px' }}
            />
          </div>
          <button className="btn-icon" onClick={loadDevices} disabled={loading} title="Обновить">
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <table>
          <thead>
            <tr>
              <th>Устройство</th>
              <th>Статус</th>
              <th>Принтер</th>
              <th>Доход / 30 дней</th>
              <th style={{ width: '140px' }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map(d => (
                <DeviceRow
                  key={d.device_id}
                  device={d}
                  onDelete={setToDelete}
                  onRequestDiag={handleRequestDiag}
                  diagReady={!!diagReady[d.device_id]}
                  fetchAuthorized={fetchAuthorized}
                  relayUrl={relayUrl}
                  onPaymentToggle={handlePaymentToggle}
                />
              ))
            ) : (
              <tr>
                <td colSpan={5} className="empty">
                  {loading ? 'Загрузка...' : 'Устройства не найдены'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
